/** Shared analytics domain types. No React, no Supabase. */

import type { DepartmentId, MetricDefinition } from "@/grid/core/types";

export type PeriodGrain = "week" | "month" | "quarter" | "year";

export type PeriodPreset =
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year";

export type CompareMode = "prev_period" | "prev_year" | "custom";

/** User-facing period selection (preset or anchored custom start). */
export interface PeriodSpec {
  preset: PeriodPreset | "custom";
  /** Week: Monday ISO. Month/quarter: first day of period. */
  customStart?: string;
  grain?: PeriodGrain;
}

export interface DateWindow {
  from: string;
  to: string;
}

export interface PeriodWindow extends DateWindow {
  grain: PeriodGrain;
  /** Anchor date for stored period rows (Monday, month 1st, quarter 1st). */
  periodStart: string;
  isPartial: boolean;
  label: string;
}

export interface CompareWindow extends DateWindow {
  mode: CompareMode;
  label: string;
}

export type ScopeKind = "org" | "team" | "rep";

export interface AnalyticsScope {
  kind: ScopeKind;
  /** Team group id (canonical team row) or rep id when drilled. */
  entityId?: number;
}

export interface TrendPoint {
  date: string;
  label?: string;
  value: number;
}

export interface ChartSeries {
  key: string;
  label: string;
  color: string;
  points: TrendPoint[];
  /** Prior-period bucket values when comparing multiple entities. */
  comparePoints?: TrendPoint[];
}

export interface ExploreScopeSelection {
  teamIds: number[];
  repIds: number[];
}

export interface EntityOption {
  id: number;
  name: string;
  /** Series/chip color key; required for teams (`teamGroupColorKey`). */
  colorKey?: string;
}

/** Per-entity share of the explore KPI total (multi-rep / multi-team). */
export interface ContributionSlice {
  key: string;
  label: string;
  color: string;
  value: number;
  sharePct: number;
}

export interface ExploreMetricView {
  metric: MetricDefinition;
  total: number | null;
  compareTotal: number | null;
  delta: number | null;
  deltaPct: number | null;
  series: ChartSeries[];
  compareSeries: TrendPoint[];
  /** Present when 2+ entities are in scope; shares of `total` for the active metric/period. */
  contributions: ContributionSlice[];
}

export interface ExploreViewModel {
  departmentId: DepartmentId;
  selection: ExploreScopeSelection;
  period: PeriodWindow;
  compare: CompareWindow;
  metricId: string;
  view: ExploreMetricView;
  teamOptions: EntityOption[];
  repOptions: EntityOption[];
  showRepPicker: boolean;
  asOf: string;
  fetchedAt: string;
}

/** Raw warehouse rows for analytics assembly. */
export interface TeamRow {
  id: number;
  name: string;
  region: string;
  department: string;
  syncore_ref: string | null;
}

export interface RepRow {
  id: number;
  team_id: number;
  full_name: string;
}

export interface DailyActivityRow {
  rep_id: number;
  activity_date: string;
  total_calls: number | null;
  connected_calls: number | null;
  meetings_held: number | null;
  sequences: number | null;
}

export interface TeamPeriodRow {
  team_id: number;
  period_type: string;
  period_start: string;
  sales_amount: number | null;
  opps_generated_count: number | null;
  opps_generated_value: number | null;
}

export interface RepPeriodRow {
  rep_id: number;
  period_type: string;
  period_start: string;
  sales_amount: number | null;
  opps_generated_count: number | null;
  opps_generated_value: number | null;
  sqls: number | null;
}

export interface AnalyticsRawData {
  teams: TeamRow[];
  reps: RepRow[];
  dailyActivities: DailyActivityRow[];
  teamPeriodMetrics: TeamPeriodRow[];
  repPeriodMetrics: RepPeriodRow[];
}

export interface DashboardDefinition {
  id: string;
  label: string;
  route: string;
  /** When set, one route per department (e.g. /explore/sales). */
  departmentScoped?: boolean;
}

export const PERIOD_PRESET_LABELS: Record<PeriodPreset, string> = {
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
};
